/*
  © 2025 Jhango
  https://www.jhango.com
*/

/*
  IMPORTANT — do not rename the checkout button's `jh-bundle-checkout` class,
  and never give it the class `btn-bundle-checkout`.

  Breeze (the third-party checkout) is configured in the merchant's own SDK
  config with window.checkoutSelector = '.btn-bundle-checkout', and its mod.js
  registers a click listener on `document` in the CAPTURE phase. Any click
  matching that selector is swallowed with preventDefault() +
  stopImmediatePropagation(), and Breeze opens checkout immediately — before the
  bundle has been added to the cart, so checkout opens on an empty cart.

  Whether Breeze's listener or this file's ran first was a genuine race between
  an async SDK and this deferred script, which is why the failure came and went
  between reloads. Naming the button something Breeze's selector cannot match
  removes the race entirely: Breeze never sees the click, and the bundle
  checkout is driven from here once the cart is confirmed updated.

  Breeze's own config is left untouched, so checkout everywhere else on the site
  keeps behaving exactly as before.
*/
;(function () {
  document.addEventListener('click', (event) => {
    const btn = event.target?.closest?.('.jh-bundle-checkout')
    if (!btn || btn.disabled) return

    const builder = btn.closest('bundle-builder')
    if (!builder || typeof builder.handleCheckout !== 'function') return

    event.preventDefault()
    event.stopImmediatePropagation()

    builder.handleCheckout(btn)
  }, true)
})()

class BundleBuilder extends HTMLElement {
  constructor () {
    super()

    this.bundleContents = JSON.parse(localStorage.getItem('ks-bundle-contents')) || []
    this.card = this.querySelector('#collection-bundle-card')

    this.buildBundle()
    this.calcPrices()
    this.handleCartStickyPosition()
    this.animateBtnBundleCheckout()
    this.initStickyMobileCard()

    this.querySelectorAll('.product-card form').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault()
        this.addToBundle(form)
      })
    })

    this.querySelectorAll('.product-card select[name="id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeProductCardVariant(select))
    })

    this.querySelectorAll('.collection-bundle-card-list .btn-close').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.removeFromBundle(btn)
      })
    })

    // NOTE: .jh-bundle-checkout clicks are handled by the document-level
    // capture listener at the top of this file, so that Breeze cannot intercept
    // them first. Do not add a per-button listener here.
  }

  addToBundle (form) {
    const btn = form.querySelector('.btn-add-to-bundle')
    const select = form.querySelector('select')
    const selectedOption = select.options[select.selectedIndex]

    if (!selectedOption || !selectedOption.dataset.variantId || selectedOption.disabled) return
    if (selectedOption.dataset.variantAvailable === 'false') return
    if (btn.disabled) return

    this.bundleContents.push({
      product_id: Number(selectedOption.dataset.productId),
      product_title: selectedOption.dataset.productTitle,
      variant_id: Number(selectedOption.dataset.variantId),
      variant_img: selectedOption.dataset.variantImg,
      variant_price: Number(selectedOption.dataset.variantPrice)
    })

    localStorage.setItem('ks-bundle-contents', JSON.stringify(this.bundleContents))

    this.buildBundle()
    this.calcPrices()

    const fullLabel = btn.querySelector('.jh-atc-full')
    const shortLabel = btn.querySelector('.jh-atc-short')

    if (fullLabel || shortLabel) {
      if (fullLabel) fullLabel.textContent = btn.dataset.textAddedToBundle
      if (shortLabel) shortLabel.textContent = 'Added ✓'
      setTimeout(() => {
        if (fullLabel) fullLabel.textContent = btn.dataset.textAddToBundle
        if (shortLabel) shortLabel.textContent = 'Add'
      }, 2000)
    } else {
      btn.textContent = btn.dataset.textAddedToBundle
      setTimeout(() => {
        btn.textContent = btn.dataset.textAddToBundle
      }, 2000)
    }
  }

  removeFromBundle (btn) {
    this.bundleContents = this.bundleContents.filter((elem, index) =>
      index !== Number(btn.dataset.number - 1))

    localStorage.setItem('ks-bundle-contents', JSON.stringify(this.bundleContents))

    this.buildBundle()
    this.calcPrices()

    this.querySelector('collection-bundle-card').focus()
  }

  buildBundle () {
    this.querySelectorAll('.collection-bundle-card-list li').forEach((elem, index) => {
      const bundleElem = this.bundleContents[index]
      const img = elem.querySelector('img')

      if (bundleElem) {
        elem.classList.add('active')
        img.setAttribute('src', bundleElem.variant_img)
        img.setAttribute('alt', bundleElem.product_title)
        img.setAttribute('data-bs-toggle', 'tooltip')
        img.setAttribute('data-bs-placement', 'top')
        img.setAttribute('data-bs-title', bundleElem.product_title)
        elem.querySelector('.btn-close').setAttribute('data-variant-id', bundleElem.variant_id)
      } else {
        elem.classList.remove('active')
        img.setAttribute('src', img.dataset.src)
        img.setAttribute('alt', '')
        img.removeAttribute('data-bs-toggle')
        img.removeAttribute('data-bs-placement')
        img.removeAttribute('data-bs-title')
        elem.querySelector('.btn-close').removeAttribute('data-variant-id')
      }
    })

    this.querySelectorAll('.collection-bundle-card-list-wrapper').forEach(elem => {
      if (Number(elem.dataset.tierQty <= this.bundleContents.length)) {
        elem.classList.add('active')
      } else {
        elem.classList.remove('active')
      }
    })

    this.querySelectorAll('.jh-bundle-checkout').forEach(btn => { btn.disabled = true })

    window.theme.bundle_builder_tiers.forEach(tier => {
      if (tier.qty === this.bundleContents.length) {
        this.querySelectorAll('.jh-bundle-checkout').forEach(btn => { btn.disabled = false })
      }
    })

    const maxQty = window.theme.bundle_builder_tiers[window.theme.bundle_builder_tiers.length - 1].qty

    if (this.bundleContents.length === maxQty) {
      this.querySelectorAll('.btn-add-to-bundle').forEach(elem => { elem.disabled = true })
    } else {
      this.querySelectorAll('.btn-add-to-bundle').forEach(elem => { elem.disabled = false })
    }
  }

  calcPrices () {
    const totalPrice = this.bundleContents.reduce((accumulator, currentValue) =>
      currentValue.variant_price + accumulator, 0)

    const currentQty = this.bundleContents.length
    let discount = 0
    let savings = 0

    window.theme.bundle_builder_tiers.forEach(tier => {
      if (tier.qty <= currentQty) {
        discount = tier.discount
      }
    })

    if (discount > 0) {
      savings = discount / 100 * totalPrice
    }

    this.querySelectorAll('[data-bundle-price]').forEach(elem => {
      elem.textContent = window.Shopify.formatMoney(totalPrice)
    })

    this.querySelectorAll('[data-you-save]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(savings)} (${discount}%)`
    })
  }

  handleCartStickyPosition () {
    if (window.matchMedia('(min-width: 800px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      if (!headerGroup) return

      const applyTop = () => {
        this.card.style.top = `${headerGroup.clientHeight + 20}px`
      }

      applyTop()
      window.addEventListener('load', applyTop)
      window.addEventListener('resize', applyTop)
      window.addEventListener('scroll', applyTop, { passive: true })
    }
  }

  onChangeProductCardVariant (select) {
    const variantImg = select[select.selectedIndex].dataset.variantImg

    if (variantImg.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImg)
    }
  }

  animateBtnBundleCheckout () {
    const animate = () => {
      this.querySelectorAll('.jh-bundle-checkout').forEach(btn => {
        btn.classList.add('animate__animated', 'animate__shakeX')
      })
      setTimeout(() => {
        this.querySelectorAll('.jh-bundle-checkout').forEach(btn => {
          btn.classList.remove('animate__animated', 'animate__shakeX')
        })
      }, 1000)
    }
    animate()

    setTimeout(() => {
      setInterval(() => {
        animate()
      }, 4000)
    }, 1000)
  }

  async handleCheckout (btn) {
    btn.classList.add('loading')
    btn.disabled = true
    btn.setAttribute('aria-busy', 'true')

    this.querySelectorAll('.bundle-checkout-error').forEach(elem => elem.remove())

    const items = []

    this.bundleContents.forEach(elem => {
      items.push({
        id: elem.variant_id,
        quantity: 1
      })
    })

    const expectedQty = items.reduce((total, item) => total + item.quantity, 0)

    try {
      if (btn.dataset.redirectToCheckout === 'true') {
        // Start a brand new cart so checkout contains the bundle and nothing
        // else. This is the same thing Breeze's own "buy now" handler does
        // before adding its product.
        this.resetCart()

        const response = await fetch(`${window.Shopify.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        })

        if (!response.ok) {
          /*
            /cart/add.js is all-or-nothing: a single rejected line (sold out, or
            a variant id that no longer exists) fails the whole bundle with 422.
            Because the bundle is persisted in localStorage, a stale entry keeps
            failing on every attempt until it is removed, so report exactly what
            Shopify rejected rather than a bare status code.
          */
          const reason = await response.json().catch(() => null)
          console.error('[bundle-builder] cart/add rejected', {
            status: response.status,
            message: reason?.message,
            description: reason?.description,
            items
          })
          throw new Error(reason?.description || reason?.message || `cart/add failed: ${response.status}`)
        }

        // Breeze resolves the cart itself when checkout opens, so the cart must
        // be readable and non-empty first — same guard its own flow applies.
        const cart = await this.waitForCartUpdate(expectedQty)

        if (!cart || !cart.item_count) {
          throw new Error('Your bundle could not be added to the cart. Please try again.')
        }

        console.info('[bundle-builder] cart ready for checkout', {
          item_count: cart.item_count,
          token: cart.token,
          items: cart.items?.map(item => `${item.title} x${item.quantity}`)
        })

        this.clearBundle()
        this.notifyCartUpdated(cart)

        await this.startCheckout()
        return
      }

      await document.querySelector('cart-container').add({ items })
      this.clearBundle()
    } catch (error) {
      console.error('[bundle-builder] checkout failed', error)
      this.showCheckoutError(btn, error)
    } finally {
      btn.classList.remove('loading')
      btn.disabled = false
      btn.setAttribute('aria-busy', 'false')
      this.buildBundle()
      this.calcPrices()
    }
  }

  // Surfaces the failure next to the button instead of silently doing nothing.
  showCheckoutError (btn, error) {
    const container = btn.closest('.collection-bundle-card-body') || btn.parentElement
    if (!container) return

    let alert = container.querySelector('.bundle-checkout-error')

    if (!alert) {
      alert = document.createElement('p')
      alert.className = 'bundle-checkout-error alert alert-danger mt-4 mb-0'
      alert.setAttribute('role', 'alert')
      container.appendChild(alert)
    }

    alert.textContent = error?.message || 'Something went wrong, please try again.'
  }

  clearBundle () {
    this.bundleContents = []
    localStorage.setItem('ks-bundle-contents', JSON.stringify(this.bundleContents))
  }

  /*
    Expires the Shopify `cart` cookie so the next /cart/add.js starts a fresh
    cart. Clicking the bundle checkout button is a direct "buy this bundle"
    action, so whatever was in the cart beforehand is intentionally dropped.
    Breeze's own buy-now handler expires the same cookie for the same reason.
  */
  resetCart () {
    try {
      const expired = new Date(Date.now() - 86400000).toUTCString()
      document.cookie = `cart=; expires=${expired}; path=/`
    } catch (error) {
      console.error('[bundle-builder] could not reset cart', error)
    }
  }

  // Polls /cart.js until the newly added items are reflected in the cart.
  async waitForCartUpdate (expectedQty, timeout = 5000) {
    const started = Date.now()
    let cart = null

    while (Date.now() - started < timeout) {
      const response = await fetch(`${window.Shopify.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      })

      if (response.ok) {
        cart = await response.json()
        if (cart.item_count >= expectedQty) return cart
      }

      await new Promise(resolve => setTimeout(resolve, 200))
    }

    return cart
  }

  // Let the theme cart and any third-party cart app pick up the new contents.
  notifyCartUpdated (cart) {
    const detail = { cart }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }))
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }))
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }))

    if (window.Shopify?.onCartUpdate) {
      try {
        window.Shopify.onCartUpdate(cart)
      } catch (error) {
        console.error('[bundle-builder] Shopify.onCartUpdate failed', error)
      }
    }
  }

  /*
    Opens the Breeze checkout exactly the way Breeze's own handlers do: log the
    click, then call startCheckout() with no argument so it resolves the cart
    from the live cart token itself. Passing a cart object here does not help —
    Breeze discards supplied cart data unless it can also read a matching `cart`
    cookie, so the cart itself has to be correct, which is what the steps above
    guarantee. Falls back to the native checkout if the SDK never loads.
  */
  async startCheckout (timeout = 5000) {
    const started = Date.now()

    while (Date.now() - started < timeout) {
      if (typeof window.breeze?.startCheckout === 'function') {
        if (typeof window.bzLogButtonClick === 'function') window.bzLogButtonClick()
        await window.breeze.startCheckout()
        return
      }
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    console.warn('[bundle-builder] breeze SDK unavailable, using native checkout')
    window.location.href = `${window.Shopify.routes.root_url}checkout`
  }

  initStickyMobileCard () {
    const stickyCard = this.querySelector('#collection-bundle-sticky-mobile-card')
    if (!stickyCard) return

    const top = document.querySelector('#collection-bundle-card').getBoundingClientRect().bottom

    window.addEventListener('scroll', () => {
      if (window.matchMedia('(min-width: 800px').matches) return

      if (window.scrollY > top) {
        stickyCard.classList.add('show')
        document.body.style.paddingBottom = stickyCard.clientHeight + 'px'
      } else {
        stickyCard.classList.remove('show')
        document.body.style.paddingBottom = 0
      }
    })

    const btnScrollBundleCard = stickyCard.querySelector('.btn-scroll-bundle-card')
    const headerHeight = document.querySelector('#header-group.sticky-top').clientHeight

    btnScrollBundleCard?.addEventListener('click', (e) => {
      window.scrollTo({
        behavior: 'smooth',
        top: document.querySelector('#collection-bundle-card').getBoundingClientRect().top - document.body.getBoundingClientRect().top - headerHeight - 16
      })
    })
  }
}
customElements.define('bundle-builder', BundleBuilder)
